/**
 * RBAC label dictionaries + area grouping for the Admin "Ruoli & Permessi" page.
 *
 * Feb 2026 v34.4 — Vista sintetica + avanzata:
 * - PERM_LABELS: chiave tecnica → etichetta italiana leggibile per admin non-tech.
 * - MODULE_LABELS: modulo tecnico → nome italiano.
 * - AREAS: raggruppamenti tematici per la Vista sintetica (ordine display).
 *
 * Backend è la fonte di verità: se un permesso non ha traduzione qui, la Vista
 * avanzata mostra la chiave tecnica come fallback (la Vista sintetica lo salta).
 */

export const PERM_LABELS = {
  // agents
  "agents.view": "Visualizza agenti",
  "agents.edit": "Modifica agenti",
  // announcements (admin_only)
  "announcements.view": "Visualizza annunci",
  "announcements.edit": "Crea/modifica annunci",
  // audit
  "audit.view": "Visualizza audit log",
  "audit.export": "Esporta audit log",
  // auth
  "auth.login": "Accesso al portale",
  // carriers
  "carriers.view": "Visualizza vettori",
  "carriers.edit": "Modifica vettori",
  "carriers.assign": "Assegna vettore a spedizione",
  "carrier_tariffs.view": "Visualizza tariffe vettori",
  "carrier_tariffs.edit": "Modifica tariffe vettori",
  // categories
  "categories.view": "Visualizza categorie clienti",
  "categories.edit": "Modifica categorie clienti",
  // contracts
  "contracts.view": "Visualizza contratti",
  "contracts.edit": "Modifica contratti",
  // customers
  "customers.view": "Visualizza clienti",
  "customers.edit": "Modifica clienti",
  "customers.block": "Blocca/sospendi clienti",
  "customers.delete": "Elimina clienti",
  "customers.view.own": "Visualizza il proprio profilo",
  // ddt
  "ddt.download": "Scarica DDT",
  "ddt.upload": "Carica DDT",
  "ddt.delete": "Elimina DDT",
  "ddt.download.own": "Scarica i propri DDT",
  // delivery
  "delivery.confirm_date": "Conferma data di consegna",
  "delivery.edit_date": "Modifica data di consegna",
  // delivery_points
  "delivery_points.view": "Visualizza punti di consegna",
  "delivery_points.edit": "Modifica punti di consegna",
  "delivery_points.view.own": "Visualizza i propri punti di consegna",
  // logistics — load plan
  "logistics.load_plan.view": "Visualizza piani di carico",
  "logistics.load_plan.view_confirmed": "Visualizza piani confermati",
  "logistics.load_plan.view_versions": "Visualizza versioni piani",
  "logistics.load_plan.create": "Crea piani di carico",
  "logistics.load_plan.edit": "Modifica piani di carico",
  "logistics.load_plan.add_shipment": "Aggiunge spedizioni al piano",
  "logistics.load_plan.remove_shipment": "Rimuove spedizioni dal piano",
  "logistics.load_plan.move_shipment": "Sposta spedizioni tra piani",
  "logistics.load_plan.confirm": "Conferma piano di carico",
  "logistics.load_plan.cancel": "Annulla piano di carico",
  "logistics.load_plan.send_to_carrier": "Invia piano al vettore",
  "logistics.load_plan.resend_to_carrier": "Reinvia piano al vettore",
  "logistics.load_plan.export": "Esporta piano (XLSX/PDF)",
  // notifications (admin_only)
  "notifications.outbox.view": "Visualizza notifiche in coda",
  "notifications.outbox.retry": "Riprova invio notifiche",
  // orders
  "orders.view": "Visualizza ordini",
  "orders.create": "Crea ordini",
  "orders.change_status": "Modifica stato ordine",
  "orders.set_processing": "Avvia lavorazione ordine",
  "orders.set_waiting_payment": "Metti in attesa pagamento",
  "orders.confirm_payment": "Conferma pagamento",
  "orders.close_residual": "Chiude residui ordine",
  "orders.cancel": "Annulla ordine",
  "orders.reopen": "Riapre ordine",
  "orders.void": "Annulla ordine post-lavorazione",
  "orders.view.own": "Visualizza i propri ordini",
  "orders.create.own": "Crea i propri ordini",
  "orders.cancel.own": "Annulla i propri ordini",
  // pfa
  "pfa.view": "Visualizza PFA",
  "pfa.edit": "Modifica obiettivi PFA",
  "pfa.view.own": "Visualizza il proprio PFA",
  // pricing
  "pricing.view": "Visualizza prezzi",
  "pricing.edit": "Modifica listini prezzi",
  "pricing.simulate": "Usa simulazione prezzi",
  "price_lists.assign": "Assegna listini a categorie",
  // products
  "products.view": "Visualizza prodotti",
  "products.edit": "Modifica prodotti",
  "products.delete": "Elimina prodotti",
  // promotions
  "promotions.view": "Visualizza promozioni",
  "promotions.edit": "Modifica promozioni",
  // rbac (admin_only)
  "roles.view": "Visualizza ruoli",
  "roles.manage": "Gestisce ruoli",
  "permissions.manage": "Concede/revoca permessi",
  // settings
  "settings.view": "Visualizza impostazioni",
  "settings.edit": "Modifica impostazioni",
  // shipments
  "shipments.view": "Visualizza spedizioni",
  "shipments.create": "Crea spedizioni",
  "shipments.edit": "Modifica spedizioni",
  "shipments.close": "Chiude spedizioni",
  "shipments.mark_departed": "Marca partenza spedizione",
  "shipments.reopen": "Riapre spedizioni",
  // tech_docs
  "tech_docs.view": "Visualizza documenti tecnici",
  "tech_docs.download": "Scarica documenti tecnici",
  "tech_docs.upload": "Carica documenti tecnici",
  "tech_docs.delete": "Elimina documenti tecnici",
  // users (admin_only)
  "users.view": "Visualizza utenti",
  "users.create": "Crea utenti",
  "users.edit": "Modifica utenti",
  "users.delete": "Elimina utenti",
  "users.manage": "Gestione completa utenti",
  // imports (admin_only, used server-side)
  "imports.execute": "Esegue importazioni dati",
};

export const MODULE_LABELS = {
  agents: "Agenti",
  announcements: "Annunci",
  audit: "Audit",
  auth: "Accesso",
  carriers: "Vettori",
  categories: "Categorie clienti",
  contracts: "Contratti",
  customers: "Clienti",
  ddt: "DDT",
  delivery: "Consegne",
  delivery_points: "Punti di consegna",
  logistics: "Logistica / Piani di carico",
  notifications: "Notifiche",
  orders: "Ordini",
  pfa: "PFA",
  pricing: "Listini e prezzi",
  products: "Prodotti",
  promotions: "Promozioni",
  rbac: "Ruoli e permessi",
  settings: "Impostazioni",
  shipments: "Spedizioni",
  tech_docs: "Documenti tecnici",
  users: "Utenti",
  imports: "Importazioni dati",
};

/**
 * Aree tematiche per la Vista sintetica. Ordine display.
 * `keys` lista i permessi (chiavi tecniche) che appartengono all'area.
 * Un permesso può comparire in più aree se logicamente rilevante.
 * Permessi admin_only e ".own" (customer-scoped) sono ESCLUSI dalla vista
 * operatore SALES/LOGISTICS (filtrati dinamicamente in AdminRbacPage).
 */
export const AREAS = [
  { key: "customers", label: "Clienti", keys: [
    "customers.view", "customers.edit", "customers.block",
  ]},
  { key: "orders", label: "Ordini", keys: [
    "orders.view", "orders.create", "orders.change_status",
    "orders.set_processing", "orders.set_waiting_payment",
    "orders.confirm_payment", "orders.close_residual", "orders.cancel",
  ]},
  { key: "pricing", label: "Listini e prezzi", keys: [
    "pricing.view", "pricing.simulate", "pricing.edit", "price_lists.assign",
  ]},
  { key: "contracts", label: "Contratti", keys: ["contracts.view", "contracts.edit"] },
  { key: "agents", label: "Agenti", keys: ["agents.view", "agents.edit"] },
  { key: "shipments", label: "Spedizioni", keys: [
    "shipments.view", "shipments.create", "shipments.edit",
    "shipments.close", "shipments.mark_departed",
  ]},
  { key: "load_plan", label: "Piani di carico", keys: [
    "logistics.load_plan.view", "logistics.load_plan.view_confirmed",
    "logistics.load_plan.view_versions", "logistics.load_plan.create",
    "logistics.load_plan.edit", "logistics.load_plan.add_shipment",
    "logistics.load_plan.remove_shipment", "logistics.load_plan.move_shipment",
    "logistics.load_plan.confirm", "logistics.load_plan.cancel",
    "logistics.load_plan.send_to_carrier", "logistics.load_plan.resend_to_carrier",
    "logistics.load_plan.export",
  ]},
  { key: "carriers", label: "Vettori", keys: [
    "carriers.view", "carriers.assign", "carriers.edit", "carrier_tariffs.view",
  ]},
  { key: "ddt", label: "DDT", keys: ["ddt.download", "ddt.upload"] },
  { key: "delivery", label: "Consegne", keys: [
    "delivery.confirm_date", "delivery.edit_date",
    "delivery_points.view", "delivery_points.edit",
  ]},
  { key: "categories", label: "Categorie clienti", keys: [
    "categories.view", "categories.edit",
  ]},
  { key: "products", label: "Prodotti", keys: ["products.view", "products.edit"] },
  { key: "pfa", label: "PFA", keys: ["pfa.view", "pfa.edit"] },
  { key: "promotions", label: "Promozioni", keys: ["promotions.view", "promotions.edit"] },
  { key: "tech_docs", label: "Documenti tecnici", keys: [
    "tech_docs.view", "tech_docs.download", "tech_docs.upload",
  ]},
  { key: "audit", label: "Audit", keys: ["audit.view"] },
  { key: "settings", label: "Impostazioni", keys: ["settings.view"] },
];

export const permLabel = (key) => PERM_LABELS[key] || key;
export const moduleLabel = (key) => MODULE_LABELS[key] || key;
